import { Button } from '@/components/ui/button';
import { MobileCarousel } from '@/components/MobileCarousel';
import { PricingCard } from '@/components/PricingCard';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Check, Shield, FileCheck, Search, Award, Clock, BadgeCheck, TrendingUp, Star } from 'lucide-react';

export default function TrademarkRegistrationPage({ onCTAClick }) {
  return (
    <div>
      {/* Service Hero */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-primary/5 to-background relative overflow-hidden" data-testid="service-hero">
        <div className="absolute inset-0 opacity-10">
          <img src="https://images.unsplash.com/photo-1619418602850-35ad20aa1700?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-widest text-accent mb-4">Trademark Registration Services</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-primary mb-6 leading-tight">
              Federal Trademark Registration Services for US Businesses
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed">
              Protect your brand name, logo, and slogan with comprehensive federal trademark registration. Our USPTO-certified experts guide you through every step of the trademark filing process, ensuring your intellectual property receives the legal protection it deserves.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white" onClick={onCTAClick} data-testid="service-hero-cta">
                Register My Trademark
              </Button>
              <Button size="lg" variant="outline" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}>
                View Pricing
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <section className="py-20 md:py-32 bg-gray-50" data-testid="service-overview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
                Complete US Trademark Registration Protection
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                US trademark registration provides nationwide legal protection for your brand identity. When you register a trademark with the United States Patent and Trademark Office (USPTO), you gain exclusive rights to use your mark in connection with your goods or services throughout the entire United States. This federal registration creates a legal presumption of ownership and gives you the ability to prevent others from using confusingly similar marks.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Our trademark registration services handle the entire USPTO filing process from start to finish. We begin with a comprehensive trademark search to identify potential conflicts, prepare and file your federal trademark application with precise specifications, monitor the application throughout the examination process, and respond to any USPTO office actions. Our experienced trademark attorneys ensure your application is filed correctly the first time, maximizing your chances of approval.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground">
                Whether you're protecting a new brand name, securing your company logo, or registering a catchy slogan, our online trademark registration services provide the expert legal support you need. We work with businesses of all sizes, from startups launching their first product to established companies expanding their trademark portfolio.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
                <img 
                  src="https://images.unsplash.com/photo-1758518730327-98070967caab"
                  alt="Professional trademark registration consultation"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why You Need This Service */}
      <section className="py-20 md:py-32" data-testid="why-need-service">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Why US Trademark Registration is Essential for Your Business
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              Federal trademark registration offers critical legal protections that every business needs to safeguard their brand identity and market position.
            </p>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white border border-border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
              <Shield className="h-12 w-12 text-accent mb-6" />
              <h3 className="text-xl font-bold text-primary mb-4">Nationwide Legal Protection</h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                Federal trademark registration grants you exclusive rights to use your mark throughout the entire United States, protecting your brand from coast to coast.
              </p>
            </div>

            <div className="bg-white border border-border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
              <BadgeCheck className="h-12 w-12 text-accent mb-6" />
              <h3 className="text-xl font-bold text-primary mb-4">Legal Presumption of Ownership</h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                USPTO registration creates a legal presumption that you own the trademark and have the exclusive right to use it, making enforcement much easier.
              </p>
            </div>

            <div className="bg-white border border-border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
              <Award className="h-12 w-12 text-accent mb-6" />
              <h3 className="text-xl font-bold text-primary mb-4">Use the ® Symbol</h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                Only federally registered trademarks can use the ® symbol, adding credibility and professionalism to your brand while deterring infringers.
              </p>
            </div>

            <div className="bg-white border border-border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
              <FileCheck className="h-12 w-12 text-accent mb-6" />
              <h3 className="text-xl font-bold text-primary mb-4">Public Record of Ownership</h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                Your trademark appears in the USPTO database, providing public notice of your rights and preventing others from registering similar marks.
              </p>
            </div>

            <div className="bg-white border border-border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
              <TrendingUp className="h-12 w-12 text-accent mb-6" />
              <h3 className="text-xl font-bold text-primary mb-4">Increased Brand Value</h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                Registered trademarks are valuable business assets that increase your company's worth and can be licensed or sold for profit.
              </p>
            </div>

            <div className="bg-white border border-border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
              <Search className="h-12 w-12 text-accent mb-6" />
              <h3 className="text-xl font-bold text-primary mb-4">Basis for International Registration</h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                US trademark registration serves as a foundation for seeking trademark protection in other countries through international treaties.
              </p>
            </div>
          </MobileCarousel>
        </div>
      </section>

      {/* Features/Benefits */}
      <section className="py-20 md:py-32 bg-gray-50" data-testid="features-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              What Our Trademark Registration Services Include
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              Comprehensive trademark filing services that cover every aspect of the USPTO registration process.
            </p>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border border-border p-8">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-3">
                <Check className="h-6 w-6 text-accent" />
                Comprehensive Trademark Search
              </h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                Before filing, we conduct an extensive search of the USPTO federal trademark database, state registries, common law sources, and business name databases to identify potential conflicts. Our detailed risk assessment helps you avoid costly rejections and legal disputes.
              </p>
            </div>

            <div className="bg-white border border-border p-8">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-3">
                <Check className="h-6 w-6 text-accent" />
                Expert Application Preparation
              </h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                Our USPTO-registered trademark attorneys prepare your application with precise trademark specifications, accurate goods and services descriptions, proper international classifications, and all required supporting documentation.
              </p>
            </div>

            <div className="bg-white border border-border p-8">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-3">
                <Check className="h-6 w-6 text-accent" />
                Federal USPTO Filing
              </h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                We handle the complete electronic filing process with the United States Patent and Trademark Office, ensuring all forms are submitted correctly and all government filing fees are paid properly to avoid delays or rejections.
              </p>
            </div>

            <div className="bg-white border border-border p-8">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-3">
                <Check className="h-6 w-6 text-accent" />
                Application Monitoring
              </h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                Throughout the 8-12 month registration process, we monitor your application status, track important deadlines, provide regular updates, and notify you of any USPTO actions or required responses.
              </p>
            </div>

            <div className="bg-white border border-border p-8">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-3">
                <Check className="h-6 w-6 text-accent" />
                Office Action Response
              </h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                If the USPTO issues an office action or refusal, our legal team reviews the issues, prepares comprehensive legal arguments, gathers supporting evidence, and submits professional responses to overcome objections.
              </p>
            </div>

            <div className="bg-white border border-border p-8">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-3">
                <Check className="h-6 w-6 text-accent" />
                Registration Certificate Delivery
              </h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                Once approved, we deliver your official trademark registration certificate, provide guidance on proper trademark use, explain maintenance requirements, and offer ongoing trademark monitoring services.
              </p>
            </div>
          </MobileCarousel>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-20 md:py-32" data-testid="service-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Our Simple 3-Step Trademark Registration Process
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              We've streamlined trademark registration to make protecting your brand as easy as possible.
            </p>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-border p-8 relative">
              <div className="absolute -top-6 left-8 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">1</div>
              <div className="mt-6">
                <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4">Submit Your Information</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  Complete our online form with your trademark details, business information, and goods or services descriptions. Our team reviews your submission and contacts you within 24 hours to discuss your specific trademark needs and answer any questions.
                </p>
              </div>
            </div>

            <div className="bg-white border border-border p-8 relative">
              <div className="absolute -top-6 left-8 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">2</div>
              <div className="mt-6">
                <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4">Search & File Your Trademark</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  We conduct a comprehensive trademark search to identify conflicts, prepare your federal trademark application with precise specifications, and electronically file your application with the USPTO. You receive confirmation and your application serial number.
                </p>
              </div>
            </div>

            <div className="bg-white border border-border p-8 relative">
              <div className="absolute -top-6 left-8 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">3</div>
              <div className="mt-6">
                <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4">Monitor & Secure Registration</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  We monitor your application throughout the USPTO examination process, respond to any office actions, keep you informed with regular updates, and deliver your official trademark registration certificate once approved by the USPTO.
                </p>
              </div>
            </div>
          </MobileCarousel>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-32" data-testid="service-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Trademark Registration Pricing Plans
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              Choose the package that fits your trademark protection needs and budget.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <PricingCard
              title="Essentials"
              price="45"
              features={[
                'Case Review',
                'Case Filing',
                'Basic Search',
                'State E-Filing',
                'Trademark Secured'
              ]}
              onCTAClick={onCTAClick}
              variant="outline"
            />
            
            <PricingCard
              title="Enhanced"
              price="149"
              features={[
                'Case Review',
                'Case Filing',
                'Direct Hit Search',
                'Trademark Alert',
                'Trademark Secured',
                'High Satisfaction Rate',
                'Refusal Risk Meter'
              ]}
              onCTAClick={onCTAClick}
              variant="outline"
            />
            
            <PricingCard
              title="Deluxe"
              price="299"
              recommended={true}
              features={[
                'Case Review',
                'Case Filing',
                'Direct Hit Search',
                'Trademark Alert',
                'Trademark Secured',
                'High Satisfaction Rate',
                'Refusal Risk Meter',
                'Trademark Monitoring',
                'Complete Documentation',
                'Digital File',
                'Dedicated Case Manager'
              ]}
              onCTAClick={onCTAClick}
            />
            
            <PricingCard
              title="Platinum"
              price="349"
              features={[
                'Case Review',
                'Case Filing',
                'Direct Hit Search',
                'Trademark Alert',
                'Trademark Secured',
                'High Satisfaction Rate',
                'Refusal Risk Meter',
                'Trademark Monitoring',
                'Complete Documentation',
                'Digital File',
                'Dedicated Case Manager',
                'High Approval Rate',
                'Comprehensive Search Report',
                'High Priority Service',
                'Cease & Desist Letter'
              ]}
              onCTAClick={onCTAClick}
              variant="outline"
            />
          </div>
        </div>
      </section>

      {/* Client Success */}
      <section className="py-20 md:py-32" data-testid="service-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Trademark Registration Success Stories
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              See how our trademark services have helped businesses protect their brands.
            </p>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-border p-8">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                "Get Logo Trademarked handled our federal trademark registration flawlessly. The comprehensive search identified potential conflicts we hadn't considered, and their expertise ensured smooth USPTO approval."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold">MB</div>
                <div>
                  <div className="font-semibold text-primary">Michael Brown</div>
                  <div className="text-sm text-muted-foreground">CEO, Tech Company</div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-border p-8">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                "Professional, efficient trademark registration service. They explained everything clearly, responded quickly to USPTO office actions, and delivered our trademark certificate on time. Highly recommend!"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold">LW</div>
                <div>
                  <div className="font-semibold text-primary">Lisa Wang</div>
                  <div className="text-sm text-muted-foreground">Founder, Fashion Brand</div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-border p-8">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                "Their online trademark registration made the process so simple. From initial consultation to receiving our registration certificate, everything was handled professionally and efficiently."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold">DP</div>
                <div>
                  <div className="font-semibold text-primary">David Park</div>
                  <div className="text-sm text-muted-foreground">Owner, Restaurant Chain</div>
                </div>
              </div>
            </div>
          </MobileCarousel>
        </div>
      </section>

      {/* Service FAQs */}
      <section className="py-20 md:py-32 bg-gray-50" data-testid="service-faqs">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Trademark Registration FAQs
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white border border-border px-8">
              <AccordionTrigger className="text-left hover:no-underline py-6">
                <span className="text-lg font-semibold text-primary">How long does US trademark registration take?</span>
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-6">
                The USPTO trademark registration process typically takes 8-12 months from filing to final registration. The timeline includes initial examination by a USPTO attorney (3-4 months), publication for opposition (30 days), and final registration. We monitor your application throughout and keep you informed of progress.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white border border-border px-8">
              <AccordionTrigger className="text-left hover:no-underline py-6">
                <span className="text-lg font-semibold text-primary">What can be registered as a trademark?</span>
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-6">
                You can register word marks (business names, slogans), design marks (logos, stylized text), combined marks (logo with text), sound marks, and even color marks if they're distinctive. The mark must be used in commerce and must not be generic, merely descriptive, or confusingly similar to existing trademarks.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white border border-border px-8">
              <AccordionTrigger className="text-left hover:no-underline py-6">
                <span className="text-lg font-semibold text-primary">Do I need a trademark attorney for USPTO filing?</span>
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-6">
                While not legally required, working with trademark professionals significantly increases your chances of approval. Our USPTO-registered attorneys ensure proper trademark classification, accurate descriptions, comprehensive searches, and professional responses to office actions, avoiding costly mistakes.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white border border-border px-8">
              <AccordionTrigger className="text-left hover:no-underline py-6">
                <span className="text-lg font-semibold text-primary">What are trademark classes and how many do I need?</span>
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-6">
                The USPTO uses 45 international trademark classes to categorize goods and services. You must file in each class where you use your mark. Most businesses file in 1-3 classes. For example, a clothing brand might file in Class 25 (apparel), while a software company files in Class 9 (downloadable software) and Class 42 (SaaS).
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-32 bg-primary text-white" data-testid="service-final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6">
            Ready to Register Your Trademark?
          </h2>
          <p className="text-lg leading-relaxed text-white/90 mb-8">
            Protect your brand with professional federal trademark registration services. Get started today!
          </p>
          <Button size="lg" onClick={onCTAClick} className="bg-accent hover:bg-accent/90 text-primary">
            Start Your Trademark Application
          </Button>
        </div>
      </section>
    </div>
  );
}
